import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SHORT = { none: "—", read: "R", write: "W" };
const TONE = {
  none: "bg-slate-100 text-slate-500",
  read: "bg-amber-50 text-amber-800",
  write: "bg-emerald-50 text-emerald-800",
};

/** ROLES-1 — every tab and sub-tab × every role. A cell shows what the role EFFECTIVELY holds;
 *  an outlined cell was set by hand, a plain one inherits from the area (or the role default).
 *  Click cycles inherit → none → read → write. Saves immediately; the backend enforces it on
 *  the next request. New tabs appear here the moment they are added to permissions.TABS. */
export function TabMatrix({ roles, tabs, tabAccess, tabOverrides, editable, onSaved }) {
  const [busy, setBusy] = useState("");
  const groups = [...new Set(tabs.map((t) => t.group))];
  const cols = roles.filter((r) => r.value !== "admin" && r.value !== "management");   // both always have everything

  const cycle = async (role, key) => {
    if (!editable) return;
    const held = tabOverrides[role]?.[key];
    const next = held === undefined ? "none" : held === "none" ? "read" : held === "read" ? "write" : null;
    setBusy(`${role}.${key}`);
    try {
      const res = await api.saveTabAccess({ [role]: { [key]: next } });
      onSaved(res);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };

  return (
    <div className="overflow-x-auto" data-testid="tab-matrix">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
            <th className="sticky left-0 bg-white py-1.5 pr-3">Tab</th>
            {cols.map((r) => <th key={r.value} className="px-1 py-1.5 text-center font-medium">{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <GroupRows key={g} group={g} rows={tabs.filter((t) => t.group === g)} cols={cols}
                       tabAccess={tabAccess} tabOverrides={tabOverrides} busy={busy} cycle={cycle} editable={editable} />
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[11px] text-slate-500">
        — hidden · R read only · W full. Outlined = set here; plain = inherited from the area
        (click cycles set-hidden → read → write → back to inherited). Admin always has everything.
      </p>
    </div>
  );
}

function GroupRows({ group, rows, cols, tabAccess, tabOverrides, busy, cycle, editable }) {
  return (
    <>
      <tr><td colSpan={cols.length + 1} className="pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">{group}</td></tr>
      {rows.map((t) => (
        <tr key={t.key} className="border-t border-slate-100" data-testid={`tab-matrix-row-${t.key}`}>
          <td className={`sticky left-0 bg-white py-1 pr-3 ${t.action ? "pl-4 italic text-slate-600" : "text-slate-800"}`}>{t.label}</td>
          {cols.map((r) => {
            const level = tabAccess[r.value]?.[t.key] || "none";
            const set = tabOverrides[r.value]?.[t.key] !== undefined;
            const id = `${r.value}.${t.key}`;
            return (
              <td key={r.value} className="px-1 py-1 text-center">
                <button type="button" disabled={!editable || busy === id} onClick={() => cycle(r.value, t.key)}
                        title={`${r.label} · ${t.label}: ${level}${set ? " (set here)" : " (inherited)"}`}
                        className={`h-6 w-9 rounded text-[11px] font-semibold transition-colors ${TONE[level]} ${set ? "ring-1 ring-slate-500" : ""} ${editable ? "hover:brightness-95" : ""}`}
                        data-testid={`tab-matrix-${r.value}-${t.key}`}>
                  {busy === id ? "…" : SHORT[level]}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
